/**
 * Owning the `AudioContext`, and getting it running without tripping the autoplay policy.
 *
 * ## The rule the browsers actually enforce
 *
 * A page may *construct* an `AudioContext` whenever it likes. What it may not do is start one
 * without a user gesture — `resume()` called from anywhere else returns a rejected promise and,
 * in Chrome, prints a warning to the console. The acceptance criterion for this milestone is
 * literally "no autoplay-policy console errors", so this file is built around one rule:
 *
 *   **`resume()` is only ever called from inside a gesture handler.**
 *
 * That is why the graph is constructed eagerly at start-up (a suspended context builds a graph
 * perfectly well, and building it early means the first sound is not also the first compile) and
 * why the only thing waiting for the user is the `resume`.
 *
 * The gesture listeners are `pointerdown`, `keydown` and `touchstart` in the capture phase, so a
 * click that lands on a menu button still counts, and they remove themselves the moment the
 * context is running. If the context was already running — some browsers, some settings, an
 * embedded context — no listeners are installed at all.
 *
 * ## Why it is allowed to be null
 *
 * Vitest has no Web Audio, and neither does a browser with audio disabled by policy. Everything
 * downstream treats a null context as "audio is off", which is a supported state rather than an
 * error path: `npm run perf` and the scripted screenshots both run with sound doing nothing, and
 * the sim is entirely playable in silence.
 *
 * `updateFixed` / `updateFrame`: neither. Start-up and one gesture, then it is out of the way.
 */

/** The subset of `AudioContext` this project uses, so a test double can stand in for it. */
export type SkySimAudioContext = AudioContext;

export interface AudioContextHandle {
  /** Null when the platform has no Web Audio, or construction failed. */
  readonly context: SkySimAudioContext | null;
  /** True once the context is actually running and sound can be heard. */
  readonly running: boolean;
  /** Stop listening for the unlock gesture. Safe to call twice. */
  dispose(): void;
}

export interface CreateAudioContextOptions {
  /** Injected by the tests; defaults to the platform constructor. */
  create?: () => SkySimAudioContext;
  /** Where to attach the unlock listeners. Defaults to the document. */
  target?: EventTarget | null;
  /** Called when the context reaches `running`, so the graph can start its long-lived voices. */
  onRunning?: () => void;
}

/**
 * Construct the context and arm the unlock.
 *
 * Never throws: a platform without `AudioContext`, or one that refuses to construct one, gets a
 * handle with a null context and the caller carries on in silence.
 */
export function createAudioContext(options: CreateAudioContextOptions = {}): AudioContextHandle {
  const constructed = construct(options.create);
  if (constructed === null) {
    return { context: null, running: false, dispose: () => {} };
  }
  // A local binding, so the closures below do not have to re-narrow a `| null` on every call.
  const context: SkySimAudioContext = constructed;

  /*
   * `undefined` means "use the document"; `null` means "no listeners at all". They are different
   * requests and `??` cannot tell them apart, which is why this is spelled out — and the trailing
   * `?? null` is because under Vitest there is no document either, so the default itself has to
   * degrade to "no listeners" rather than to a crash.
   */
  const requested =
    options.target === undefined
      ? (globalThis.document as EventTarget | undefined)
      : options.target;
  const target: EventTarget | null = requested ?? null;
  let disposed = false;

  const handle = {
    context,
    get running(): boolean {
      return context.state === 'running';
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      removeListeners();
    },
  };

  const events = ['pointerdown', 'keydown', 'touchstart'] as const;

  function removeListeners(): void {
    if (target === null) return;
    for (const name of events) target.removeEventListener(name, unlock, true);
  }

  function unlock(): void {
    if (disposed) return;
    /*
     * `void` rather than `await`, and a `catch` that swallows: this runs inside a gesture
     * handler, and a rejected resume here means the browser has decided the gesture did not
     * count. There is nothing useful to do about that except leave the listeners armed for the
     * next one, which is exactly what happens — `removeListeners` is only reached on success.
     */
    void context
      .resume()
      .then(() => {
        if (context.state !== 'running') return;
        removeListeners();
        options.onRunning?.();
      })
      .catch(() => {
        /* not this gesture, then. */
      });
  }

  if (context.state === 'running') {
    options.onRunning?.();
  } else if (target !== null) {
    // Capture phase, so a click consumed by a menu button still unlocks the audio.
    for (const name of events) target.addEventListener(name, unlock, true);
  }

  return handle;
}

function construct(create?: () => SkySimAudioContext): SkySimAudioContext | null {
  try {
    if (create !== undefined) return create();
    const ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
    if (ctor === undefined) return null;
    // `interactive` asks for the smallest buffer the device can sustain. This is a flight
    // simulator: a gun report that arrives 90 ms after the trigger is a gun report that feels
    // broken, and the extra CPU is a rounding error next to the render budget.
    return new ctor({ latencyHint: 'interactive' });
  } catch {
    return null;
  }
}
